import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Mail, X, Sparkles, TrendingUp, TrendingDown, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal that fetches /api/creator/digest/preview and renders a faithful copy
 * of the email a creator would receive every Monday — plus a "Send to me now"
 * CTA so creators can try the real send.
 */
export default function DigestPreviewModal({ open, onClose }) {
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const r = await api.get("/creator/digest/preview");
        if (!cancelled) setDigest(r.data);
      } catch (e) {
        console.warn("[DigestPreview] failed", e);
        if (!cancelled) toast.error("Couldn't load your digest");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/creator/digest/send-me");
      if (r.data.sent) {
        toast.success("Sent! Check your inbox in a minute.");
      } else if (r.data.mock) {
        toast.message("Email delivery is in fallback mode", {
          description: "Resend domain not yet verified — preview shown here is exactly what you'd receive.",
        });
      } else {
        toast.error("Couldn't send right now");
      }
    } catch {
      toast.error("Couldn't send right now");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  const d = digest;
  const deltaPct = d?.reactions_delta_pct ?? 0;
  const deltaPositive = deltaPct >= 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-center justify-center p-4"
        style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(10px)" }}
        onClick={onClose}
        data-testid="digest-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-heavy w-full max-w-xl rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
          data-testid="digest-modal"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#DD2A7B] font-semibold flex items-center gap-1.5">
                <Mail size={11} /> Weekly Digest · Preview
              </div>
              <h2 className="font-display text-2xl font-bold mt-1">Your week, at a glance</h2>
            </div>
            <button
              onClick={onClose}
              data-testid="digest-close"
              className="text-[#94A3B8] hover:text-[#0F172A] p-1 rounded-full hover:bg-slate-100"
            >
              <X size={18} />
            </button>
          </div>

          {busy && !d ? (
            <div className="py-10 text-center text-[#94A3B8] text-sm">Crunching your week…</div>
          ) : !d ? (
            <div className="py-10 text-center text-[#94A3B8] text-sm">No digest available.</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2.5">
                <StatTile
                  label="Reactions"
                  value={d.reactions_this_week}
                  color="#0EA5E9"
                  footer={
                    <span className={`text-[11px] font-bold flex items-center gap-0.5 ${deltaPositive ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                      {deltaPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                      {deltaPositive ? "+" : ""}{deltaPct}% WoW
                    </span>
                  }
                />
                <StatTile label="Posts" value={d.posts_this_week} color="#DD2A7B" footer={
                  <span className="text-[11px] text-[#94A3B8]">vs {d.posts_prev_week} prev</span>
                }/>
                <StatTile label="Revenue" value={`$${(d.revenue_net_week || 0).toFixed(2)}`} color="#F59E0B" footer={
                  <span className="text-[11px] text-[#94A3B8]">net</span>
                }/>
              </div>

              {d.best_post && (
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4" data-testid="digest-best-post">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#DD2A7B] font-bold mb-2">Best post this week</div>
                  <div className="text-sm text-[#0F172A] leading-snug mb-2">{d.best_post.content || <span className="italic text-[#94A3B8]">— no caption —</span>}</div>
                  <div className="text-xs text-[#64748B]">
                    <span className="font-bold text-[#0EA5E9]">{d.best_post.reactions}</span> reactions
                    <span className="mx-1 text-[#CBD5E1]">·</span>
                    <span className="font-bold text-[#DD2A7B]">{d.best_post.engagement_per_hour}</span>/hr engagement
                  </div>
                </div>
              )}

              {d.ai_suggestion && (
                <div
                  className="rounded-2xl p-4 border"
                  style={{
                    background: "linear-gradient(135deg, rgba(255,138,101,0.10), rgba(221,42,123,0.10), rgba(129,52,175,0.10))",
                    borderColor: "rgba(221,42,123,0.20)",
                  }}
                  data-testid="digest-ai-suggestion"
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8134AF] font-bold mb-2 flex items-center gap-1">
                    <Sparkles size={10} /> What to ship next · Claude Sonnet 4.5
                  </div>
                  <div className="text-sm text-[#0F172A] leading-relaxed font-medium">{d.ai_suggestion}</div>
                </div>
              )}

              {!d.best_post && !d.ai_suggestion && (
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-[#64748B] text-center">
                  Publish a post or earn a reaction to unlock your first AI-coached digest.
                </div>
              )}

              <button
                onClick={sendNow}
                disabled={sending}
                data-testid="digest-send-now"
                className="w-full nexus-btn-primary flex items-center justify-center gap-2"
              >
                <Send size={14} /> {sending ? "Sending…" : "Send to my inbox now"}
              </button>
              <div className="text-[10px] text-[#94A3B8] text-center">
                Every Monday morning, this lands in your inbox automatically.
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function StatTile({ label, value, color, footer }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
      <div className="text-[9px] uppercase tracking-[0.15em] text-[#64748B] font-semibold">{label}</div>
      <div className="font-display font-bold text-xl mt-0.5" style={{ color }}>{value}</div>
      <div className="mt-0.5">{footer}</div>
    </div>
  );
}
